import { useCallback, useEffect, useState } from 'react'
import type { TenantBootstrapModel } from '@unified-lk/tenant-config'
import type { AuthSession } from '../../types/plans'

// ── Types ────────────────────────────────────────────────────────────────────

interface ImagingPrimaryReport {
  primary_count: number
  counts: { no_ct_no_opg: number; no_ct_with_opg: number; with_ct: number }
  sections: Array<{ title: string; rows: Array<Record<string, unknown>> }>
}

interface MedicalCardRecord {
  record_id: number
  patient_id: number
  doctor_id: number | null
  patient_name: string
  doctor_name: string
  record_date: string
  record_type: string
  summary: string
}

interface DoctorStat {
  doctor_id: number | null
  doctor_name: string
  record_count: number
  share_percent: number
}

interface MedicalCardsResponse {
  records: MedicalCardRecord[]
  doctors: DoctorStat[]
  summary: { records: number; patients: number; doctors: number }
}

type TabId = 'imaging' | 'medcards'

// ── Helpers ──────────────────────────────────────────────────────────────────

function fmt(date: Date) { return date.toISOString().slice(0, 10) }
function monthStart() { const n = new Date(); return fmt(new Date(n.getFullYear(), n.getMonth(), 1)) }
function today() { return fmt(new Date()) }
function canSeeAllMedCards(p: string[]) { const s = new Set(p); return s.has('users') || s.has('reports_mcard') }

// ── Main component ────────────────────────────────────────────────────────────

export function StaffReportsSection({
  bootstrap,
  session,
}: {
  bootstrap: TenantBootstrapModel
  session: AuthSession
}) {
  const [activeTab, setActiveTab] = useState<TabId>('imaging')

  const permissions: string[] = (session.user.permissions as string[]) ?? []
  const seeAll = canSeeAllMedCards(permissions)
  const linkedDoctorId = (session.user as { linked_doctor_id?: number | null }).linked_doctor_id ?? null

  const TABS: { id: TabId; label: string; shortLabel: string }[] = [
    { id: 'imaging',   label: 'КТ / ОПТГ по первичным',    shortLabel: 'КТ / ОПТГ' },
    { id: 'medcards',  label: seeAll ? 'Медкарты — все врачи' : 'Медкарты — мои', shortLabel: 'Медкарты' },
  ]

  return (
    <div className="grid gap-4">
      {/* ── Tab switcher ── */}
      <div className="flex overflow-hidden rounded-[18px] border border-slate-200 bg-slate-50 p-1">
        {TABS.map((tab) => (
          <button
            key={tab.id}
            type="button"
            onClick={() => setActiveTab(tab.id)}
            className={[
              'flex-1 rounded-[14px] px-3 py-2.5 text-sm font-medium transition-all',
              activeTab === tab.id
                ? 'bg-white text-slate-900 shadow-sm'
                : 'text-slate-500 hover:text-slate-700',
            ].join(' ')}
          >
            <span className="hidden sm:inline">{tab.label}</span>
            <span className="sm:hidden">{tab.shortLabel}</span>
          </button>
        ))}
      </div>

      {/* ── Panels ── */}
      {activeTab === 'imaging'  && <ImagingPanel  bootstrap={bootstrap} session={session} />}
      {activeTab === 'medcards' && <MedCardsPanel bootstrap={bootstrap} session={session} seeAll={seeAll} linkedDoctorId={linkedDoctorId} />}
    </div>
  )
}

// ── Imaging panel ─────────────────────────────────────────────────────────────

function ImagingPanel({ bootstrap, session }: { bootstrap: TenantBootstrapModel; session: AuthSession }) {
  const [dateFrom, setDateFrom] = useState(monthStart)
  const [dateTo,   setDateTo]   = useState(today)
  const [report,   setReport]   = useState<ImagingPrimaryReport | null>(null)
  const [loading,  setLoading]  = useState(false)
  const [error,    setError]    = useState<string | null>(null)

  const load = useCallback(async () => {
    setLoading(true); setError(null)
    try {
      const p = new URLSearchParams({ date_from: dateFrom, date_to: dateTo, tenant_id: 'ulybkapro', sync_primary: 'true' })
      const res = await fetch(`${bootstrap.apiBase}/staff/reports/imaging-primary?${p}`, {
        headers: { Accept: 'application/json', Authorization: `Bearer ${session.accessToken}` },
      })
      const json = (await res.json()) as { success: boolean; report?: ImagingPrimaryReport; message?: string; error?: string }
      if (!json.success) { setError(json.message ?? json.error ?? 'Ошибка'); return }
      setReport(json.report ?? null)
    } catch { setError('Ошибка сети.') }
    finally { setLoading(false) }
  }, [bootstrap.apiBase, session.accessToken, dateFrom, dateTo])

  useEffect(() => { void load() }, [load])

  return (
    <ReportCard accent="sky" label="Диагностика" title="КТ / ОПТГ по первичным пациентам">
      <Filters
        dateFrom={dateFrom} dateTo={dateTo}
        onDateFrom={setDateFrom} onDateTo={setDateTo}
        onApply={() => void load()} loading={loading} accent="sky"
      />

      {error ? <ErrBox message={error} onRetry={() => void load()} /> :
       loading ? <Spin /> :
       !report ? <Empty /> : (
        <div className="grid gap-5">
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            <Tile label="Первичных"   value={report.primary_count}          color="sky" />
            <Tile label="Без КТ/ОПТГ" value={report.counts.no_ct_no_opg}   color="slate" />
            <Tile label="ОПТГ без КТ" value={report.counts.no_ct_with_opg} color="amber" />
            <Tile label="С КТ"        value={report.counts.with_ct}         color="emerald" />
          </div>
          {report.sections.map((sec, i) => (
            <div key={i} className="grid gap-2">
              {sec.title && <h3 className="text-sm font-semibold text-slate-700">{sec.title}</h3>}
              {sec.rows.length > 0 && <SimpleTable rows={sec.rows} />}
              {sec.rows.length === 0 && <p className="text-sm text-slate-400">Пациенты отсутствуют.</p>}
            </div>
          ))}
        </div>
      )}
    </ReportCard>
  )
}

// ── MedCards panel ────────────────────────────────────────────────────────────

function MedCardsPanel({
  bootstrap, session, seeAll, linkedDoctorId,
}: {
  bootstrap: TenantBootstrapModel
  session: AuthSession
  seeAll: boolean
  linkedDoctorId: number | null
}) {
  const [dateFrom, setDateFrom] = useState(monthStart)
  const [dateTo,   setDateTo]   = useState(today)
  const [data,     setData]     = useState<MedicalCardsResponse | null>(null)
  const [loading,  setLoading]  = useState(false)
  const [error,    setError]    = useState<string | null>(null)

  const load = useCallback(async () => {
    setLoading(true); setError(null)
    try {
      const p = new URLSearchParams({ from_date: dateFrom, to_date: dateTo, limit: '500', only_unfilled: '1' })
      if (!seeAll && linkedDoctorId) p.set('doctor_id', String(linkedDoctorId))
      const res = await fetch(`${bootstrap.apiBase}/staff/patients/medical-cards?${p}`, {
        headers: { Accept: 'application/json', Authorization: `Bearer ${session.accessToken}` },
      })
      const json = (await res.json()) as { success: boolean } & Partial<MedicalCardsResponse> & { message?: string; error?: string }
      if (!json.success) { setError(json.message ?? json.error ?? 'Ошибка'); return }
      setData({ records: json.records ?? [], doctors: json.doctors ?? [], summary: json.summary ?? { records: 0, patients: 0, doctors: 0 } })
    } catch { setError('Ошибка сети.') }
    finally { setLoading(false) }
  }, [bootstrap.apiBase, session.accessToken, dateFrom, dateTo, seeAll, linkedDoctorId])

  useEffect(() => { void load() }, [load])

  return (
    <ReportCard accent="violet" label="Медкарты" title={seeAll ? 'Незаполненные медкарты — все врачи' : 'Незаполненные медкарты — мои'}>
      <Filters
        dateFrom={dateFrom} dateTo={dateTo}
        onDateFrom={setDateFrom} onDateTo={setDateTo}
        onApply={() => void load()} loading={loading} accent="violet"
      />

      {error ? <ErrBox message={error} onRetry={() => void load()} /> :
       loading ? <Spin /> :
       !data ? <Empty /> :
       data.records.length === 0 ? (
        <div className="rounded-[18px] border border-emerald-100 bg-emerald-50 px-4 py-3 text-sm text-emerald-700">
          ✓ Незаполненных медкарт за период не найдено.
        </div>
      ) : (
        <div className="grid gap-5">
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
            <Tile label="Записей"   value={data.summary.records}  color="slate" />
            <Tile label="Пациентов" value={data.summary.patients} color="violet" />
            {seeAll && <Tile label="Врачей" value={data.summary.doctors} color="slate" />}
          </div>

          {seeAll && data.doctors.length > 0 && (
            <div className="grid gap-2">
              <h3 className="text-sm font-semibold text-slate-700">По врачам</h3>
              <div className="grid gap-1">
                {data.doctors.map((d) => (
                  <div key={`${d.doctor_id ?? ''}_${d.doctor_name}`} className="flex items-center gap-3 rounded-[12px] bg-slate-50 px-4 py-2">
                    <span className="flex-1 truncate text-sm text-slate-700">{d.doctor_name}</span>
                    <span className="text-sm font-semibold text-slate-900">{d.record_count}</span>
                    <span className="w-10 shrink-0 text-right text-xs text-slate-400">{d.share_percent}%</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div>
            <h3 className="mb-2 text-sm font-semibold text-slate-700">Записи</h3>
            <div className="overflow-x-auto rounded-[18px] border border-slate-200">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="bg-slate-50">
                    <Th>Пациент</Th>
                    {seeAll && <Th>Врач</Th>}
                    <Th>Дата</Th>
                    <Th>Сводка</Th>
                  </tr>
                </thead>
                <tbody>
                  {data.records.map((r, i) => (
                    <tr key={r.record_id} className={i % 2 === 0 ? 'bg-white' : 'bg-slate-50/50'}>
                      <td className="px-4 py-2 text-slate-800">{r.patient_name}</td>
                      {seeAll && <td className="px-4 py-2 text-slate-600">{r.doctor_name}</td>}
                      <td className="px-4 py-2 tabular-nums text-slate-500 whitespace-nowrap">{r.record_date}</td>
                      <td className="max-w-[200px] truncate px-4 py-2 text-slate-500" title={r.summary}>{r.summary || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </ReportCard>
  )
}

// ── Shared UI ─────────────────────────────────────────────────────────────────

function ReportCard({
  accent, label, title, children,
}: {
  accent: 'sky' | 'violet'
  label: string
  title: string
  children: React.ReactNode
}) {
  const labelColor = accent === 'sky' ? 'text-sky-600' : 'text-violet-600'
  return (
    <section className="rounded-[28px] border border-slate-200/70 bg-white p-4 shadow-[0_18px_42px_rgba(15,23,42,0.08)] sm:p-6">
      <p className={`mb-1 text-xs font-bold uppercase tracking-[0.14em] ${labelColor}`}>{label}</p>
      <h2 className="mb-5 text-lg font-semibold text-slate-900 sm:text-xl">{title}</h2>
      {children}
    </section>
  )
}

function Filters({
  dateFrom, dateTo, onDateFrom, onDateTo, onApply, loading, accent,
}: {
  dateFrom: string; dateTo: string
  onDateFrom: (v: string) => void; onDateTo: (v: string) => void
  onApply: () => void; loading: boolean; accent: 'sky' | 'violet'
}) {
  const ring  = accent === 'sky' ? 'focus:border-sky-300 focus:ring-sky-100'      : 'focus:border-violet-300 focus:ring-violet-100'
  const btn   = accent === 'sky' ? 'border-sky-100 bg-sky-50 text-sky-800 hover:bg-sky-100'   : 'border-violet-100 bg-violet-50 text-violet-800 hover:bg-violet-100'
  return (
    <div className="mb-5 flex flex-wrap items-end gap-2">
      <label className="grid gap-1">
        <span className="text-xs font-medium text-slate-600">Дата с</span>
        <input type="date" value={dateFrom} onChange={(e) => onDateFrom(e.target.value)}
          className={`rounded-[14px] border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 outline-none transition focus:ring-2 ${ring}`} />
      </label>
      <label className="grid gap-1">
        <span className="text-xs font-medium text-slate-600">Дата по</span>
        <input type="date" value={dateTo} onChange={(e) => onDateTo(e.target.value)}
          className={`rounded-[14px] border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 outline-none transition focus:ring-2 ${ring}`} />
      </label>
      <button type="button" disabled={loading} onClick={onApply}
        className={`rounded-[14px] border px-4 py-2 text-sm font-medium transition disabled:cursor-progress disabled:opacity-60 ${btn}`}>
        {loading ? 'Загружаем...' : 'Применить'}
      </button>
    </div>
  )
}

function Tile({ label, value, color }: { label: string; value: number; color: 'sky' | 'slate' | 'amber' | 'emerald' | 'violet' }) {
  const s: Record<string, string> = {
    sky:     'border-sky-100 bg-sky-50 text-sky-600',
    slate:   'border-slate-200 bg-slate-50 text-slate-500',
    amber:   'border-amber-100 bg-amber-50 text-amber-600',
    emerald: 'border-emerald-100 bg-emerald-50 text-emerald-600',
    violet:  'border-violet-100 bg-violet-50 text-violet-600',
  }
  return (
    <article className={`grid gap-1 rounded-[20px] border px-4 py-3 ${s[color]}`}>
      <span className="text-[11px] font-medium uppercase tracking-[0.1em]">{label}</span>
      <strong className="text-2xl font-bold text-slate-900">{value}</strong>
    </article>
  )
}

function SimpleTable({ rows }: { rows: Array<Record<string, unknown>> }) {
  if (!rows.length) return null
  return (
    <div className="overflow-x-auto rounded-[18px] border border-slate-200">
      <table className="min-w-full text-sm">
        <thead>
          <tr className="bg-slate-50">
            {Object.keys(rows[0]).map((col) => <Th key={col}>{col}</Th>)}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr key={i} className={i % 2 === 0 ? 'bg-white' : 'bg-slate-50/50'}>
              {Object.values(row).map((v, vi) => (
                <td key={vi} className="px-4 py-2 text-slate-700">{v == null ? '—' : String(v)}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

function Th({ children }: { children: React.ReactNode }) {
  return (
    <th className="px-4 py-2 text-left text-xs font-semibold uppercase tracking-[0.08em] text-slate-500">
      {children}
    </th>
  )
}

function ErrBox({ message, onRetry }: { message: string; onRetry: () => void }) {
  return (
    <div className="rounded-[18px] border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
      {message}
      <button type="button" className="ml-3 underline" onClick={onRetry}>Повторить</button>
    </div>
  )
}

function Spin() { return <div className="text-sm text-slate-500">Загружаем данные...</div> }
function Empty() { return <div className="text-sm text-slate-500">Нет данных.</div> }
